import React from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { useProfile, useSpendingBreakdown } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const CashFlow = () => {
  const { data: profile, loading: profileLoading, error: profileError, refresh: refreshProfile } = useProfile();
  const { data: spendingData, loading: spendingLoading, error: spendingError, refresh: refreshSpending } = useSpendingBreakdown();

  const loading = profileLoading || spendingLoading;
  const error = profileError || spendingError;
  const refresh = () => {
    refreshProfile();
    refreshSpending();
  };

  if (loading) return <LoadingState message="Loading cash flow data..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!profile || !spendingData) return null;

  const monthlyIncome = profile.income / 12;
  const monthlySpending = spendingData.total;
  const netCashFlow = monthlyIncome - monthlySpending;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Cash Flow</Text>
      
      <View style={styles.cardsContainer}>
        <View style={styles.incomeCard}>
          <Text style={styles.cardTitle}>Monthly Income</Text>
          <Text style={styles.incomeAmount}>${monthlyIncome.toLocaleString()}</Text>
          <Text style={styles.cardSubtitle}>Annual: ${profile.income.toLocaleString()}</Text>
        </View>
        
        <View style={styles.spendingCard}>
          <Text style={styles.cardTitle}>Monthly Spending</Text>
          <Text style={styles.spendingAmount}>${monthlySpending.toLocaleString()}</Text>
          <Text style={styles.cardSubtitle}>Across {spendingData.categories.length} categories</Text>
        </View>
      </View>
      
      <View style={styles.netCashFlowCard}>
        <Text style={styles.cardTitle}>Net Cash Flow</Text>
        <Text style={[
          styles.netCashFlowAmount,
          netCashFlow >= 0 ? styles.positiveAmount : styles.negativeAmount
        ]}>
          {netCashFlow >= 0 ? '+' : ''}${netCashFlow.toLocaleString()}
        </Text>
        <Text style={styles.cashFlowDescription}>
          {netCashFlow >= 0
            ? `You have $${netCashFlow.toLocaleString()} available for savings or investments each month.`
            : `Your spending exceeds your income by $${Math.abs(netCashFlow).toLocaleString()} each month.`}
        </Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 768, // max-w-3xl equivalent
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 24,
  },
  cardsContainer: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 32,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  incomeCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  spendingCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 8,
  },
  incomeAmount: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#059669',
    marginBottom: 8,
  },
  spendingAmount: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#DC2626',
    marginBottom: 8,
  },
  cardSubtitle: {
    fontSize: 14,
    color: '#6B7280',
  },
  netCashFlowCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  netCashFlowAmount: {
    fontSize: 30,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  positiveAmount: {
    color: '#059669',
  },
  negativeAmount: {
    color: '#DC2626',
  },
  cashFlowDescription: {
    fontSize: 14,
    color: '#6B7280',
  },
});

export default CashFlow; 